import { IWatrRequest, IWatrRequestTravels } from '@nkr/models/index';
import { WageAndTravelReimbursementRequestStatus } from '@nkr/enums/watr-reimbursement-request-status';
import { ITripGroup } from '../types/trip.types';
import { getMileageRateForDate, resolveMileageRate } from '../../year-rates';
import { IPriorTotals } from '../../prior-totals';
import { resolveDerProgramCap, shouldExcludeTravelFromStandardDerPrior } from '../../advanced-reimbursement-status-buckets';

export interface IGrandTotal {
    pendingApprovedTotal: number;
    currentlyApprovedTotal: number;
    caregiverPendingApprovedTotal: number;
    caregiverCurrentlyApprovedTotal: number;
}

export const recalculateGrandTotal = (groups: ITripGroup[]): IGrandTotal => {
    const pendingApprovedTotal = groups
        .filter(g => g.status === 'pending')
        .reduce((sum, g) => {
            const lineItemsTotal = g.lineItems
                .filter(item => !item.isCaregiverExpense)
                .reduce((itemSum, item) => itemSum + item.amount, 0);
            return sum + lineItemsTotal;
        }, 0);

    const currentlyApprovedTotal = groups
        .filter(g => g.status === 'approved')
        .reduce((sum, g) => {
            const lineItemsTotal = g.lineItems
                .filter(item => !item.isCaregiverExpense)
                .reduce((itemSum, item) => itemSum + item.amount, 0);
            return sum + lineItemsTotal;
        }, 0);

    const caregiverPendingApprovedTotal = groups
        .filter(g => g.status === 'pending')
        .reduce((sum, g) => {
            const lineItemsTotal = g.lineItems
                .filter(item => item.isCaregiverExpense === true)
                .reduce((itemSum, item) => itemSum + item.amount, 0);
            return sum + lineItemsTotal;
        }, 0);

    const caregiverCurrentlyApprovedTotal = groups
        .filter(g => g.status === 'approved')
        .reduce((sum, g) => {
            const lineItemsTotal = g.lineItems
                .filter(item => item.isCaregiverExpense === true)
                .reduce((itemSum, item) => itemSum + item.amount, 0);
            return sum + lineItemsTotal;
        }, 0);

    return { 
        pendingApprovedTotal, 
        currentlyApprovedTotal,
        caregiverPendingApprovedTotal,
        caregiverCurrentlyApprovedTotal
    };
};

export const getPriorTotals = (data: IWatrRequest): IPriorTotals => {
    const statusList = [
        WageAndTravelReimbursementRequestStatus.AdminApproved,
        WageAndTravelReimbursementRequestStatus.Reimbursed
    ];

    const priorTravel = data.requestTravels.filter(x =>
        statusList.indexOf(x.status) >= 0
        && !shouldExcludeTravelFromStandardDerPrior(data, x)
    );

    const priorTotals: IPriorTotals = {
        miles: 0,
        carServiceTrain: 0,
        meals: 0,
        dependentCare: 0,
        der: 0,
        caregiverLodging: 0,
        caregiverMeals: 0,
        caregiverTransportation: 0,
        caregiverFlight: 0,
        caregiverDependentCare: 0,
        caregiverTotal: 0
    };

    priorTravel.forEach(x => {
        const reimbursementRate = getMileageRateForDate(x.createdDateTime);
        const reimbursementAmount = (x.roundTripMiles ?? 0) * reimbursementRate;
        const roundTrip = x.isMilesOverWritten ? (x.overrideMiles ?? 0) : (x.roundTripMiles ?? 0);
        const meals = x.isMealsOverWritten ? (x.overrideMeals ?? 0) : (x.mealExpense ?? 0);

        priorTotals.miles += roundTrip;
        priorTotals.carServiceTrain += x.carServiceExpense ?? 0;
        priorTotals.meals += meals;
        priorTotals.dependentCare += x.dependentCareExpense ?? 0;
        priorTotals.der += x.hotelLodgingExpense + x.flightAndTrainExpense + reimbursementAmount + x.carServiceExpense + meals + x.dependentCareExpense;
    });

    data.otherRequests.forEach(x => {
        const reimbursementRate = resolveMileageRate(x.reimbursementRate, x.createdDate);
        const reimbursementAmount = (x.roundTripMiles ?? 0) * reimbursementRate;

        priorTotals.miles += x.roundTripMiles ?? 0;
        priorTotals.carServiceTrain += x.carServiceExpense ?? 0;
        priorTotals.meals += x.mealExpense ?? 0;
        priorTotals.dependentCare += x.dependentCareExpense ?? 0;
        priorTotals.der += x.hotelLodgingExpense + x.flightAndTrainExpense + reimbursementAmount + x.carServiceExpense + x.mealExpense + x.dependentCareExpense;
    });

    return priorTotals;
};

export interface ITripStats {
    previousReimbursementAmount: number;
    currentlyApproved: number;
    currentPendingReimbursementAmount: number;
    derLimit: number;
}

export const calculateTripStats = (
    tripGroup: ITripGroup | undefined,
    data: IWatrRequest,
    categoryCappedTotal?: number
): ITripStats => {
    if (!tripGroup) {
        return {
            previousReimbursementAmount: 0,
            currentlyApproved: 0,
            currentPendingReimbursementAmount: 0,
            derLimit: 0
        };
    }

    const lineItemsTotal = tripGroup.lineItems.reduce((sum, item) => sum + item.amount, 0);

    const tripPriorTotals: IPriorTotals = {
        miles: 0,
        carServiceTrain: 0,
        meals: 0,
        dependentCare: 0,
        der: 0,
        caregiverLodging: 0,
        caregiverMeals: 0,
        caregiverTransportation: 0,
        caregiverFlight: 0,
        caregiverDependentCare: 0,
        caregiverTotal: 0
    };

    const statusList = [
        WageAndTravelReimbursementRequestStatus.AdminApproved,
        WageAndTravelReimbursementRequestStatus.Reimbursed
    ];

    const priorTravel = data.requestTravels.filter(x =>
        statusList.indexOf(x.status) >= 0
        && !shouldExcludeTravelFromStandardDerPrior(data, x)
    );

    priorTravel.forEach(x => {
        const reimbursementRate = getMileageRateForDate(x.createdDateTime);
        const reimbursementAmount = (x.roundTripMiles ?? 0) * reimbursementRate;
        const roundTrip = x.isMilesOverWritten ? (x.overrideMiles ?? 0) : (x.roundTripMiles ?? 0);
        const meals = x.isMealsOverWritten ? (x.overrideMeals ?? 0) : (x.mealExpense ?? 0);
        tripPriorTotals.miles += roundTrip;
        tripPriorTotals.carServiceTrain += x.carServiceExpense ?? 0;
        tripPriorTotals.meals += meals;
        tripPriorTotals.dependentCare += x.dependentCareExpense ?? 0;
        tripPriorTotals.der += x.hotelLodgingExpense + x.flightAndTrainExpense + reimbursementAmount + x.carServiceExpense + meals + x.dependentCareExpense;
    });

    data.otherRequests.forEach(x => {
        const reimbursementRate = resolveMileageRate(x.reimbursementRate, x.createdDate);
        const reimbursementAmount = (x.roundTripMiles ?? 0) * reimbursementRate;
        tripPriorTotals.miles += x.roundTripMiles ?? 0;
        tripPriorTotals.carServiceTrain += x.carServiceExpense ?? 0;
        tripPriorTotals.meals += x.mealExpense ?? 0;
        tripPriorTotals.dependentCare += x.dependentCareExpense ?? 0;
        tripPriorTotals.der += x.hotelLodgingExpense + x.flightAndTrainExpense + reimbursementAmount + x.carServiceExpense + x.mealExpense + x.dependentCareExpense;
    });

    const tripDerLimit = Math.round(resolveDerProgramCap(data) - tripPriorTotals.der);
    const remainingDerLimit = Math.max(0, tripDerLimit);
    const baseTotal = categoryCappedTotal != null && !isNaN(categoryCappedTotal)
        ? categoryCappedTotal
        : lineItemsTotal;
    const cappedTotal = Math.min(Math.max(0, baseTotal), remainingDerLimit);

    let tripCurrentlyApproved = 0;
    let tripCurrentPendingReimbursementAmount = 0;

    if (tripGroup.status === 'approved') {
        tripCurrentlyApproved = cappedTotal;
    } else if (tripGroup.status === 'pending') {
        tripCurrentPendingReimbursementAmount = cappedTotal;
    }

    return {
        previousReimbursementAmount: tripPriorTotals.der,
        currentlyApproved: tripCurrentlyApproved,
        currentPendingReimbursementAmount: tripCurrentPendingReimbursementAmount,
        derLimit: tripDerLimit
    };
};

